import { useState, type ReactNode } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { CloneStatus, Health, Severity } from '@api/api';
import { HEALTH_COLOR, HEALTH_LABEL, SEVERITY_CHIP, STATUS_CHIP, STATUS_LABEL } from '../lib/status';
import { fmtDateTime, relTime } from '../lib/format';
import { useNow } from '../lib/useNow';
import { errorMessage } from '../api/client';

export function HealthDot({ health, label = false, size = 10 }: { health: Health; label?: boolean; size?: number }) {
  const dot = (
    <Box
      component="span"
      role="img"
      aria-label={`Health: ${HEALTH_LABEL[health]}`}
      sx={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: HEALTH_COLOR[health],
        flexShrink: 0,
        boxShadow: health === 'down' ? `0 0 0 3px ${HEALTH_COLOR.down}33` : undefined,
        animation: health === 'provisioning' ? 'scPulse 1.4s ease-in-out infinite' : undefined,
        '@keyframes scPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
      }}
    />
  );
  if (!label) return <Tooltip title={HEALTH_LABEL[health]}>{dot}</Tooltip>;
  return (
    <Stack direction="row" spacing={1} alignItems="center" component="span">
      {dot}
      <Typography component="span" variant="body2" fontWeight={600}>
        {HEALTH_LABEL[health]}
      </Typography>
    </Stack>
  );
}

export function StatusChip({ status }: { status: CloneStatus }) {
  return <Chip size="small" variant="outlined" color={STATUS_CHIP[status]} label={STATUS_LABEL[status]} />;
}

export function SeverityChip({ severity }: { severity: Severity }) {
  return <Chip size="small" color={SEVERITY_CHIP[severity]} label={severity} sx={{ textTransform: 'capitalize' }} />;
}

export function RelTime({ iso, prefix }: { iso: string | null | undefined; prefix?: string }) {
  const now = useNow();
  if (!iso) return <span>—</span>;
  return (
    <Tooltip title={fmtDateTime(iso)}>
      <span>
        {prefix}
        {relTime(iso, now)}
      </span>
    </Tooltip>
  );
}

export function PageHeader({ title, subtitle, actions, eyebrow }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; eyebrow?: ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 3 }}>
      <Box sx={{ minWidth: 0 }}>
        {eyebrow && (
          <Typography variant="overline" color="text.secondary" component="div">
            {eyebrow}
          </Typography>
        )}
        <Typography variant="h4" component="h1" sx={{ wordBreak: 'break-word' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {actions}
        </Stack>
      )}
    </Stack>
  );
}

export function EmptyState({ title, text, icon, action }: { title: string; text?: ReactNode; icon?: ReactNode; action?: ReactNode }) {
  return (
    <Stack alignItems="center" spacing={1} sx={{ py: 6, px: 2, textAlign: 'center', color: 'text.secondary' }}>
      <Box sx={{ fontSize: 40, opacity: 0.6, display: 'flex' }}>{icon ?? <InboxOutlinedIcon fontSize="inherit" />}</Box>
      <Typography variant="subtitle1" color="text.primary" fontWeight={600}>
        {title}
      </Typography>
      {text && <Typography variant="body2" sx={{ maxWidth: 420 }}>{text}</Typography>}
      {action && <Box sx={{ pt: 1 }}>{action}</Box>}
    </Stack>
  );
}

export function ErrorState({ error, onRetry, title = 'Could not load' }: { error: unknown; onRetry?: () => void; title?: string }) {
  return (
    <MuiAlert
      severity="error"
      icon={<ErrorOutlineIcon />}
      action={
        onRetry && (
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        )
      }
      sx={{ my: 2 }}
    >
      <strong>{title}.</strong> {errorMessage(error)}
    </MuiAlert>
  );
}

export function CopyButton({ text, label = 'Copy', size = 'small' }: { text: string; label?: string; size?: 'small' | 'medium' }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };
  return (
    <Tooltip title={done ? 'Copied' : label}>
      <IconButton size={size} onClick={copy} aria-label={label}>
        {done ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

/** A value shown exactly once (passwords, tokens). */
export function OneTimeSecret({ title, value, children }: { title: string; value: string; children?: ReactNode }) {
  return (
    <MuiAlert severity="warning" variant="outlined" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
      <Typography fontWeight={700}>{title}</Typography>
      {children}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
        <Typography sx={{ fontFamily: 'monospace', fontSize: '1.1rem', flex: 1, wordBreak: 'break-all' }} data-testid="one-time-secret">
          {value}
        </Typography>
        <CopyButton text={value} />
      </Stack>
      <Typography variant="body2" sx={{ mt: 1 }}>
        It is shown once. Copy it now and keep it in a password manager.
      </Typography>
    </MuiAlert>
  );
}

export function SectionCard({ title, action, children, dense }: { title?: ReactNode; action?: ReactNode; children: ReactNode; dense?: boolean }) {
  return (
    <Card sx={{ height: '100%' }}>
      {title && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 1.5, pb: dense ? 0.5 : 1 }}>
          <Typography variant="subtitle2" component="h2">
            {title}
          </Typography>
          {action}
        </Stack>
      )}
      <CardContent sx={{ pt: title ? 0.5 : 2, '&:last-child': { pb: 2 } }}>{children}</CardContent>
    </Card>
  );
}

export function SkeletonRows({ rows = 5, height = 36 }: { rows?: number; height?: number }) {
  return (
    <Stack spacing={1} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} variant="rounded" height={height} />
      ))}
    </Stack>
  );
}

export function KeyValue({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'minmax(110px, max-content) 1fr', columnGap: 2, rowGap: 0.75, m: 0 }}>
      {items.map(([k, v], i) => (
        <Box key={i} sx={{ display: 'contents' }}>
          <Typography component="dt" variant="body2" color="text.secondary">
            {k}
          </Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, minWidth: 0, wordBreak: 'break-word' }}>
            {v}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
